import { createApi } from '@reduxjs/toolkit/query/react'
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { selectCrsToken, selectDomain } from '../../slices/metadata/selectors'
import type { RootState } from '../../createStore'
import { hydrate } from '../../actions'
import {
  schemas,
  LoginResponse,
  GroupManagerResponse,
  GroupLogoUpdatePayload,
  GroupLogoUpdateResponse,
  CompanyListResponse,
  DashboardListPayload,
  DashboardListResponse,
  DashboardReportChartResponse,
  DashboardReportChartPayload,
  ValidateCommonPasswordPayload,
  DrillDashboardReportChartPayload,
  DrillDashboardReportChartResponse,
  CategoryListResponse,
  ArticleListResponse,
  ArticleListPayload,
  UserArticleListResponse,
  UserArticleListPayload,
  ArticlesSearchResponse,
  ArticlesSearchPayload,
  VoteResponse,
  VotePayload,
  TopicListResponse,
  CreateTicketResponse,
  CreateTicketPayload,
  ClientConfigurationResponse,
  EnableMfaResponse,
  EnableMfaPayload,
  ResendMfaSmaResponse,
  ResendMfaSmsPayload,
  VerifyMfaCodePayload,
  DisableMfaResponse,
  NewUser,
  EditUser,
  DeleteUserPayload,
  DeleteUserResponse,
  ManageProfileRequest,
  VerifyTokenResponse,
  VerifyTokenPayload,
  UserAssetPayload,
  UserAssetResponse,
  ValidateResetTokenResponse,
  ssoLinkResponse,
  ClientConfigurationPayload,
} from './types'
import { TAG } from './constants'
import {
  VerifyBackupCodePayload,
  VerifyBackupCodeResponse,
  VerifyDisableMfaPayload,
  VerifyDisableMfaResponse,
  VerifyMfaCodeResponse,
} from './types'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { getEnvVar } from '@client-hub/shared/utils/env'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { EnvironmentVariable } from '@client-hub/shared/constants/env'
import { API_REQUEST_TIMEOUT } from '../../constants'
import createFetchWithTimeout from '../../utils/createFetchWithTimeout'

export const crsApi = createApi({
  reducerPath: 'crsApi',
  baseQuery: fetchBaseQuery({
    fetchFn: createFetchWithTimeout(API_REQUEST_TIMEOUT),
    baseUrl: getEnvVar(EnvironmentVariable.CRS_API_URL),
    prepareHeaders: (headers, { getState }) => {
      const rootState = getState() as RootState
      const token = selectCrsToken(rootState)
      let domain = selectDomain(rootState)
      if (!domain?.includes('icanbwell')) {
        domain = 'bwell.hub.dev.icanbwell.com'
      }
      headers.set('env', getEnvVar(EnvironmentVariable.ENVIRONMENT))
      headers.set('domain', domain)
      if (token) {
        headers.set('Authorization', `Token ${token}`)
      }
      return headers
    },
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === hydrate.toString()) {
      return (action['payload'] as any)[reducerPath]
    }
    return undefined
  },
  tagTypes: [...Object.values(TAG)],
  endpoints: (build) => ({
    userLogin: build.mutation<LoginResponse, schemas['LoginRequest']>({
      query: ({ username, password }) => {
        const body = new FormData()
        body.append('username', username)
        body.append('password', password)
        return { url: '/login/', method: 'POST', body }
      },
    }),
    userLogout: build.mutation<void, { token: string }>({
      query: ({ token }) => {
        return { url: '/logout/', method: 'POST', headers: { token } }
      },
    }),
    groupManager: build.query<GroupManagerResponse, void>({
      query: () => '/group-manager',
      providesTags: [TAG.GROUP_MANAGER],
    }),
    groupLogo: build.mutation<GroupLogoUpdateResponse, GroupLogoUpdatePayload>({
      query: ({ groupId, logoFile }) => {
        const body = new FormData()
        body.append('group_logo', logoFile)
        body.append('id', groupId.toString())
        return { url: '/group-logo-update/', method: 'POST', body }
      },
      invalidatesTags: [TAG.GROUP_MANAGER],
    }),
    companyList: build.query<CompanyListResponse, void>({
      query: () => '/company-list',
      providesTags: [TAG.COMPANY_LIST],
    }),
    dashboardList: build.query<DashboardListResponse, DashboardListPayload>({
      query: ({ groupId }) => ({
        url: 'dashboard/',
        params: { group: groupId },
      }),
      providesTags: [TAG.DASHBOARD_LIST],
    }),
    dashboardReportChart: build.query<DashboardReportChartResponse, DashboardReportChartPayload>({
      query: ({ groupId, dashboardId }) => ({
        url: '/dashboard-url/',
        params: {
          group: groupId,
          dashboard_id: dashboardId,
        },
      }),
      providesTags: [TAG.DASHBOARD_REPORT_CHART],
      keepUnusedDataFor: 0, // Disable caching for this query
    }),
    drillDashboardReportChart: build.mutation<DrillDashboardReportChartResponse, DrillDashboardReportChartPayload>({
      query: (data) => {
        const formData: FormData = new FormData()
        const filter_values = JSON.stringify(data.filter_values)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        formData.append('destination_dashboard_id', data.destination_dashboard_id!)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        formData.append('event_type', data.event_type!)
        formData.append('filter_values', filter_values)
        return { url: '/drilldown-dashboard/', method: 'POST', formData }
      },
      invalidatesTags: [TAG.DASHBOARD_REPORT_CHART],
    }),
    manageProfile: build.mutation<LoginResponse, ManageProfileRequest>({
      query: (data) => {
        const body = new FormData()
        Object.keys(data).forEach((key: keyof typeof data) => body.append(key, data[key]))
        return { url: '/manage-profile/', method: 'POST', body }
      },
    }),
    validateCommonPassword: build.mutation<void, ValidateCommonPasswordPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/validate-common-password/',
        body: data,
      }),
    }),
    accountsProfile: build.query<LoginResponse, void>({
      query: () => '/accounts/profile',
    }),
    categoryList: build.query<CategoryListResponse, void>({
      query: () => '/category-list',
      providesTags: [TAG.CATEGORY_LIST],
    }),
    articles: build.query<ArticleListResponse, void>({
      query: () => '/articles',
      providesTags: [TAG.ARTICLES],
    }),
    articleList: build.query<ArticleListResponse, ArticleListPayload>({
      query: ({ id }) => ({
        url: '/category-article-list',
        params: { id },
      }),
      providesTags: [TAG.ARTICLE_LIST],
    }),
    userArticleList: build.query<UserArticleListResponse, UserArticleListPayload>({
      query: ({ id }) => ({
        url: '/user-articles',
        params: { id },
      }),
      providesTags: [TAG.USER_ARTICLE_LIST],
    }),
    articlesSearch: build.query<ArticlesSearchResponse, ArticlesSearchPayload>({
      query: ({ search }) => ({
        url: '/articles-search',
        params: { search },
      }),
      providesTags: [TAG.ARTICLES_SEARCH],
    }),
    vote: build.mutation<VoteResponse, VotePayload>({
      query: (data) => {
        const body = new FormData()
        Object.keys(data).forEach((key: keyof typeof data) => body.append(key, data[key]))
        return { method: 'POST', url: '/vote-change/', body }
      },
      onQueryStarted({ id, vote }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          crsApi.util.updateQueryData('userArticleList', { id }, (draft) => {
            Object.assign(draft.results[0], { votes: vote })
          }),
        )
        queryFulfilled.catch(patchResult.undo)
      },
      invalidatesTags: [TAG.USER_ARTICLE_LIST],
    }),
    topicList: build.query<TopicListResponse, void>({
      query: () => '/topic-list',
      providesTags: [TAG.TOPIC_LIST],
    }),
    createTicket: build.mutation<CreateTicketResponse, CreateTicketPayload>({
      query: ({ id, message }) => {
        const body = new FormData()
        body.append('id', id)
        body.append('message', message)
        return { method: 'POST', url: '/create-ticket/', body }
      },
    }),
    clientConfiguration: build.query<ClientConfigurationResponse, void>({
      query: () => '/clients/configuration',
      providesTags: [TAG.CLIENT_CONFIGURATION],
    }),
    updateClientConfiguration: build.mutation<ClientConfigurationResponse, ClientConfigurationPayload>({
      query: ({ body }) => ({ method: 'PUT', url: '/clients/configuration/', body }),
      invalidatesTags: [TAG.CLIENT_CONFIGURATION],
    }),
    enableMfa: build.mutation<EnableMfaResponse, EnableMfaPayload>({
      query: (body) => {
        return { method: 'POST', url: '/accounts/multi-factor-auth/enable/', body }
      },
      invalidatesTags: [TAG.CLIENT_CONFIGURATION],
    }),
    resendMfaSms: build.mutation<ResendMfaSmaResponse, ResendMfaSmsPayload>({
      query: (body) => ({ method: 'POST', url: '/accounts/resend-sms/', body }),
    }),
    verifyMfaCode: build.mutation<VerifyMfaCodeResponse, VerifyMfaCodePayload>({
      query: ({ setup, ...body }) => ({
        method: 'POST',
        url: '/accounts/multi-factor-auth/verify/',
        body,
        params: { setup },
      }),
    }),
    disableMfa: build.mutation<DisableMfaResponse, void>({
      query: () => ({ method: 'POST', url: '/accounts/multi-factor-auth/disable/' }),
    }),
    verifyDisableMfa: build.mutation<VerifyDisableMfaResponse, VerifyDisableMfaPayload>({
      query: (body) => ({ method: 'PUT', url: '/accounts/multi-factor-auth/disable/verify/', body }),
    }),
    verifyBackupCode: build.mutation<VerifyBackupCodeResponse, VerifyBackupCodePayload>({
      query: (body) => ({ method: 'POST', url: '/accounts/multi-factor-auth/verify-backup-code/', body }),
    }),
    sessionUpdate: build.mutation<void, { token: string }>({
      query: ({ token }) => ({ method: 'POST', url: '/session_update/', headers: { token } }),
    }),
    ssoLogin: build.mutation<
      ssoLinkResponse,
      {
        username: string | undefined
        config?: {
          isInternalUser?: boolean
        }
      }
    >({
      query: ({ username, config }) => {
        const body = new FormData()
        let url = '/oauth_provider/sso-redirect/'

        if (username) body.append('email', username)
        if (config?.isInternalUser) {
          url += '?is_internal_user=true'
        }
        // more conditionals can be added if needed.

        return { url, method: 'POST', body }
      },
    }),
    addTeamMember: build.mutation<schemas['AddTeamMemberResponseSchema'], NewUser>({
      query: ({ team_membership, ...data }) => {
        const body = new FormData()
        Object.keys(data).forEach((key: keyof typeof data) => {
          return body.append(key, data[key] as any)
        })
        const teamMembership = team_membership.map((item) => ({
          ...item,
          is_admin: new String(item.is_admin),
        }))
        body.append('status', 'active')
        body.append('is_locked', 'false')
        body.append('team_membership', JSON.stringify(teamMembership))

        return { method: 'POST', url: '/add-team-member/', body: body }
      },
      invalidatesTags: [TAG.GROUP_MANAGER],
    }),
    forgotPassword: build.mutation<unknown, { email: string }>({
      query: (body) => ({ method: 'POST', url: '/forgot-password/', body }),
    }),
    validateResetToken: build.mutation<ValidateResetTokenResponse, { token: string }>({
      query: (body) => ({ method: 'POST', url: '/validate-reset-token/', body }),
    }),
    resetPassword: build.mutation<LoginResponse, { token: string; password: string; confirm_password: string }>({
      query: (body) => ({ method: 'POST', url: '/user-reset-password/', body }),
    }),
    editTeamMember: build.mutation<schemas['EditTeamMemberResponseSchema'], EditUser>({
      query: ({ team_membership, ...data }) => {
        const body = new FormData()
        Object.keys(data).forEach((key: keyof typeof data) => {
          return body.append(key, data[key] as any)
        })
        const teamMembership = team_membership.map((item) => ({
          ...item,
          is_admin: new String(item.is_admin),
        }))
        body.append('team_membership', JSON.stringify(teamMembership))

        return { method: 'POST', url: '/edit-team-member/', body: body }
      },
      invalidatesTags: [TAG.GROUP_MANAGER],
    }),
    deleteTeamMember: build.mutation<DeleteUserResponse, DeleteUserPayload>({
      query: ({ groupId, userId }) => ({
        method: 'GET',
        url: '/delete-team-member/',
        params: {
          user: userId,
          id: groupId,
        },
      }),
      invalidatesTags: [TAG.GROUP_MANAGER],
    }),
    verifyToken: build.mutation<VerifyTokenResponse, VerifyTokenPayload>({
      query: (payload) => ({
        method: 'POST',
        url: '/verify-token/',
        body: payload,
      }),
    }),
    userAsset: build.mutation<UserAssetResponse, UserAssetPayload>({
      query: ({ itemId, itemType, userId, groupId }) => {
        return {
          method: 'POST',
          url: '/accounts/user-asset/',
          body: {
            asset_id: itemId,
            asset_type: itemType,
            user_id: userId,
            group_id: groupId,
          },
        }
      },
    }),
  }),
})

export const {
  useUserLoginMutation,
  useUserLogoutMutation,
  useGroupManagerQuery,
  useGroupLogoMutation,
  useCompanyListQuery,
  useDashboardListQuery,
  useDashboardReportChartQuery,
  useDrillDashboardReportChartMutation,
  useManageProfileMutation,
  useValidateCommonPasswordMutation,
  useCategoryListQuery,
  useArticlesQuery,
  useArticleListQuery,
  useUserArticleListQuery,
  useArticlesSearchQuery,
  useVoteMutation,
  useTopicListQuery,
  useCreateTicketMutation,
  useClientConfigurationQuery,
  useEnableMfaMutation,
  useResendMfaSmsMutation,
  useVerifyMfaCodeMutation,
  useDisableMfaMutation,
  useVerifyDisableMfaMutation,
  useVerifyBackupCodeMutation,
  useAddTeamMemberMutation,
  useSessionUpdateMutation,
  useForgotPasswordMutation,
  useValidateResetTokenMutation,
  useResetPasswordMutation,
  useEditTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useVerifyTokenMutation,
  useUserAssetMutation,
  useAccountsProfileQuery,
  useSsoLoginMutation,
  useLazyAccountsProfileQuery,
  useUpdateClientConfigurationMutation,
} = crsApi
